import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { scorePassword, STRENGTH_META } from "@/lib/passwordStrength";

export default function ResetPassword() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError(t("password_min_chars"));
      return;
    }
    if (password !== confirm) {
      setError(t("passwords_no_match"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("reset_password_success"));
      nav("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-md py-10">
        <h1 className="mb-6 text-3xl font-bold tracking-tight">{t("reset_password_title")}</h1>
        <Card className="border-border">
          <CardContent className="p-6 space-y-4">
            <div
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="reset-password-missing-token"
            >
              {t("reset_token_missing")}
            </div>
            <Link
              to="/forgot-password"
              className="block text-center text-sm text-primary underline-offset-4 hover:underline"
              data-testid="request-new-link"
            >
              {t("forgot_password")}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md py-10">
      <div className="label-caps mb-2">{t("reset_password_title")}</div>
      <h1 className="mb-6 text-3xl font-bold tracking-tight">{t("reset_password_title")}</h1>
      <Card className="border-border">
        <CardContent className="p-6">
          <form className="space-y-4" onSubmit={submit}>
            <p className="text-sm text-muted-foreground">{t("reset_password_hint")}</p>
            <div>
              <Label htmlFor="password">{t("new_password")}</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input"
              />
              {password && (
                <div className="mt-2" data-testid="password-strength">
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{t("password_strength")}</span>
                    <span
                      className="font-medium"
                      data-testid="password-strength-label"
                    >
                      {t(STRENGTH_META[scorePassword(password)].key)}
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full transition-all ${STRENGTH_META[scorePassword(password)].color}`}
                      style={{ width: `${STRENGTH_META[scorePassword(password)].widthPct}%` }}
                      data-testid="password-strength-bar"
                    />
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="confirm">{t("confirm_password")}</Label>
              <Input
                id="confirm"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-password-confirm-input"
              />
            </div>
            {error && (
              <div
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="reset-password-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="reset-password-submit-btn"
            >
              {loading ? "…" : t("reset_password_submit")}
            </Button>
            <div className="text-center text-sm">
              <Link
                to="/login"
                className="text-primary underline-offset-4 hover:underline"
                data-testid="back-to-login-link"
              >
                {t("back_to_login")}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
